import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { briefApi } from '../services/api';
import { DailyBrief, BriefListItem, MarketMover } from '../types';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const formatBriefDate = (date: string) =>
  format(parseISO(date), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });

const MoversRow: React.FC<{ title: string; movers: MarketMover[]; positive: boolean }> = ({
  title,
  movers,
  positive,
}) => (
  <div>
    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
    <div className="space-y-1.5">
      {movers.map((mover) => (
        <div key={mover.symbol} className="flex items-center justify-between text-sm">
          <span className="font-semibold text-gray-800">
            {mover.symbol} <span className="text-gray-400 font-normal">{mover.name}</span>
          </span>
          <span className={positive ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
            {mover.changePct > 0 ? '+' : ''}
            {mover.changePct.toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  </div>
);

const BulletSection: React.FC<{ title: string; icon: string; items: string[] }> = ({ title, icon, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl shadow p-6 mb-6">
      <h2 className="text-lg font-bold text-gray-900 mb-3">
        {icon} {title}
      </h2>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-2 text-gray-700 leading-relaxed text-[15px]">
            <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Página do resumo diário: /resumo-diario (mais recente) e /resumo-diario/:date. */
export const ResumoDiarioPage: React.FC = () => {
  const { date } = useParams<{ date?: string }>();
  const [brief, setBrief] = useState<DailyBrief | null>(null);
  const [archive, setArchive] = useState<BriefListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const load = date ? briefApi.getByDate(date) : briefApi.getLatest();
    Promise.all([load.catch(() => null), briefApi.getArchive(30).catch(() => [])])
      .then(([briefData, archiveData]) => {
        setBrief(briefData);
        setArchive(archiveData);
      })
      .finally(() => setLoading(false));
  }, [date]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Carregando resumo do mercado...</div>
      </div>
    );
  }

  if (!brief) {
    return (
      <>
        <Helmet>
          <title>O Mercado em 3 Minutos | {SITE_NAME}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Resumo ainda não disponível</h1>
            <p className="text-gray-600 mb-4">
              O resumo diário do mercado é publicado todas as manhãs. Volte em breve.
            </p>
            <Link to="/" className="text-blue-600 hover:underline">Voltar para o início</Link>
          </div>
        </div>
      </>
    );
  }

  const canonicalUrl = date
    ? `${SITE_URL}/resumo-diario/${brief.date}`
    : `${SITE_URL}/resumo-diario`;
  const pageTitle = `${brief.title} — O Mercado em 3 Minutos (${format(parseISO(brief.date), 'dd/MM/yyyy')})`;
  const metaDescription = brief.summary.slice(0, 158);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: brief.title,
    description: metaDescription,
    url: canonicalUrl,
    datePublished: brief.createdAt,
    dateModified: brief.updatedAt,
    inLanguage: 'pt-BR',
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} | {SITE_NAME}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/resumo-diario" className="hover:text-blue-600 transition">Resumo Diário</Link>
            {date && (
              <>
                <span>›</span>
                <span className="text-gray-700">{format(parseISO(brief.date), 'dd/MM/yyyy')}</span>
              </>
            )}
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-xl shadow p-8 mb-6 text-white">
                <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-2">
                  ⚡ O mercado em 3 minutos
                </p>
                <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">{brief.title}</h1>
                <p className="text-blue-100 text-sm capitalize">{formatBriefDate(brief.date)}</p>
              </div>

              {/* Summary */}
              <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                {brief.summary.split(/\n\n+/).map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4 last:mb-0 text-[15px]">
                    {paragraph}
                  </p>
                ))}
                <p className="text-xs text-gray-400 mt-4 pt-4 border-t leading-relaxed">
                  🤖 Resumo editorial produzido com auxílio de IA a partir das notícias das últimas 24h e de
                  dados de mercado da CoinGecko, conforme nossa{' '}
                  <Link to="/politica-editorial" className="underline hover:text-blue-600">Política Editorial</Link>.
                  Não é recomendação de investimento.
                </p>
              </div>

              <BulletSection title="Fatos relevantes" icon="📌" items={brief.keyFacts} />
              <BulletSection title="Riscos a acompanhar" icon="⚠️" items={brief.watchList} />
              <BulletSection title="Calendário do dia" icon="📅" items={brief.calendar} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {(brief.gainers?.length || brief.losers?.length) ? (
                <div className="bg-white rounded-xl shadow p-6 space-y-5">
                  <h2 className="text-lg font-bold text-gray-900">Movimentos em 24h</h2>
                  {brief.gainers && brief.gainers.length > 0 && (
                    <MoversRow title="Principais altas" movers={brief.gainers} positive />
                  )}
                  {brief.losers && brief.losers.length > 0 && (
                    <MoversRow title="Principais quedas" movers={brief.losers} positive={false} />
                  )}
                  <p className="text-xs text-gray-400">
                    Fonte: CoinGecko · dados do momento da publicação ·{' '}
                    {format(new Date(brief.updatedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              ) : null}

              {/* Archive */}
              {archive.length > 0 && (
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Edições anteriores</h2>
                  <div className="space-y-2">
                    {archive
                      .filter((item) => item.date !== brief.date)
                      .slice(0, 10)
                      .map((item) => (
                        <Link
                          key={item.date}
                          to={`/resumo-diario/${item.date}`}
                          className="block p-2 rounded hover:bg-gray-50 transition"
                        >
                          <p className="text-xs font-bold text-blue-600">
                            {format(parseISO(item.date), 'dd/MM/yyyy')}
                          </p>
                          <p className="text-sm text-gray-700 line-clamp-1">{item.title}</p>
                        </Link>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
